import * as React from 'react'
import { Box, Checkbox, Form, InputLabel } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar, ConfirmDeleteModal } from 'components'
import { Grid, Typography } from 'cs-kevlar-react'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { TablePicker } from '../TablePicker'
import { EditAdminUserForm, EditAdminUserFormSchema } from './EditAdminUserForm'
import { UserViewModel, LicensedAgencyViewModel } from '../../api'
import { CustomerAdmin, User } from 'contexts/base'
import { determineDefaultPhone, determineDefaultEmail } from 'utils/users'
import { useAuth } from 'hooks/useAuth'
import { useAgency } from 'hooks/useAgency'

interface EditAdminUserProps {
  user: CustomerAdmin
  agency: LicensedAgencyViewModel
  onClose: () => void
  availableUsers: User[]
  onSave: (values: CustomerAdmin, agenciesToAdmin: string[]) => void
  onAdd: (values: CustomerAdmin, agenciesToAdmin: string[]) => void
  onDelete: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingBottom: theme.spacing(8),
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(10),
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  }),
)

const userSearchColumns = [
  {
    label: 'Name',
    sortable: false,
    sortByDefault: false,
    filterable: true,
    dataProperty: 'fullName' as keyof UserViewModel,
  },
  {
    label: 'Username',
    sortable: false,
    sortByDefault: false,
    filterable: true,
    dataProperty: 'name' as keyof UserViewModel,
  },
]

export const EditAdminUser: React.FunctionComponent<EditAdminUserProps> = ({
  user,
  agency,
  onClose,
  availableUsers,
  onSave,
  onAdd,
  onDelete,
}) => {
  const classes = useStyles({})

  const {
    user: authenticatedUser,
    authorizedAgencies: authorizedAgencies,
    agencies: agencies,
  } = useAuth()
  const agencyHook = useAgency()

  React.useEffect(() => {
    const loadAgencyData = async () => {
      await agencyHook.getAuthorizedAgencies()
      await agencyHook.getAgencies()
    }

    if (
      authenticatedUser !== null &&
      (authenticatedUser.isSupport ||
        (authenticatedUser.authorizedLicensedAgencies !== null &&
          authenticatedUser.authorizedLicensedAgencies.length > 1))
    ) {
      loadAgencyData()
    }
  }, [authenticatedUser])

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(
    false,
  )

  const initialUser = user.id === 'NEW' ? null : user
  const [selectedUser, setSelectedUser] = React.useState<CustomerAdmin | null>(
    initialUser,
  )

  const isAdding = user.id === 'NEW'
  const isEditing = user.id !== 'NEW'

  const [selectedAgenciesToAdmin, setSelectedAgenciesToAdmin] = React.useState<
    string[]
  >(
    initialUser === null || initialUser.authorizedLicensedAgencies === null
      ? []
      : initialUser.authorizedLicensedAgencies,
  )

  const onEdit = (values: CustomerAdmin) => {
    if (user.id === 'NEW') {
      onAdd(values, selectedAgenciesToAdmin)
    } else {
      onSave(values, selectedAgenciesToAdmin)
    }
  }

  const onSelectUser = (id: string) => {
    const matchedUsers = availableUsers.filter(
      (availableUser) => availableUser.id === id,
    )

    if (matchedUsers.length > 0) {
      const matchedUser = matchedUsers[0]
      const selectedUser: CustomerAdmin = {
        id: '',
        customerUserId: matchedUser.id,
        userId: matchedUser.id,
        name: matchedUser.name,
        fullName: matchedUser.fullName,
        phone: determineDefaultPhone(matchedUser),
        email: determineDefaultEmail(matchedUser),
        cadEmails: matchedUser.emailAddresses?.map((x) => x.email) ?? [],
        isActive: matchedUser.isActive,
        licensedAgencyId: agency.licensedAgencyId,
        licensedAgencyName: '',
        customerAgencyId: '',
        authorizedLicensedAgencies: [],
      }

      setSelectedUser(selectedUser)
      setSelectedAgenciesToAdmin(selectedUser.authorizedLicensedAgencies)
    }
  }

  const onDeleteClicked = () => {
    setIsConfirmingDelete(false)
    onDelete()
  }

  const onSearch = () => {
    if (isAdding) {
      setSelectedUser(null)
      setSelectedAgenciesToAdmin([])
    }
  }

  const selectedUserWithDefaults = React.useMemo(() => {
    if (selectedUser === null) return null
    const userWithDefaults: CustomerAdmin = {
      ...selectedUser,
      email: selectedUser.email ?? '',
      cadEmails: selectedUser.cadEmails ?? [],
    }

    return userWithDefaults
  }, [selectedUser])

  const toggleAgencyToAdmin = (agencyId: string) => {
    if (selectedAgenciesToAdmin.includes(agencyId)) {
      setSelectedAgenciesToAdmin([
        ...selectedAgenciesToAdmin.filter((x) => x !== agencyId),
      ])
    } else {
      setSelectedAgenciesToAdmin([...selectedAgenciesToAdmin, agencyId])
    }
  }

  return (
    <React.Fragment>
      {selectedUserWithDefaults === null && (
        <Box className={classes.root}>
          <TablePicker
            availableItems={availableUsers}
            onSelect={onSelectUser}
            searchLabel="Search"
            columns={userSearchColumns}
            uniqueIdentifier="AddAdminUserPicker"
            singularAdjective="user"
            pluralAdjective="users"
            ariaLabel="Select User"
          />
        </Box>
      )}
      {selectedUserWithDefaults !== null && (
        <Formik
          initialValues={selectedUserWithDefaults}
          validationSchema={EditAdminUserFormSchema}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid
                  container
                  spacing={Spacing}
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                  className={classes.formFields}
                >
                  <EditAdminUserForm
                    formikProps={formikProps}
                    allowSearch={isAdding}
                    onSearch={onSearch}
                  />

                  {authenticatedUser !== null &&
                    (authenticatedUser.isSupport ||
                      (authenticatedUser.authorizedLicensedAgencies !== null &&
                        authenticatedUser.authorizedLicensedAgencies.length >
                          1)) && (
                      <Grid item xs={12} key="AgenciesToAdmin">
                        <InputLabel shrink>Agencies to Admin</InputLabel>

                        {agencies.map((agency) => {
                          return (
                            <Grid
                              key={`AgencyToAdmin::${agency.licensedAgencyId}`}
                              xs={12}
                              item
                            >
                              <Checkbox
                                label={agency.name}
                                id={agency.licensedAgencyId}
                                disabled={
                                  selectedUser?.licensedAgencyId ===
                                    agency.licensedAgencyId ||
                                  (!authenticatedUser.isSupport &&
                                    authorizedAgencies.filter(
                                      (x) =>
                                        x.licensedAgencyId ==
                                        agency.licensedAgencyId,
                                    ).length == 0)
                                }
                                checked={
                                  selectedUser?.licensedAgencyId ===
                                    agency.licensedAgencyId ||
                                  (selectedAgenciesToAdmin !== null &&
                                    selectedAgenciesToAdmin.includes(
                                      agency.licensedAgencyId,
                                    ))
                                }
                                onChange={() =>
                                  toggleAgencyToAdmin(agency.licensedAgencyId)
                                }
                              />
                            </Grid>
                          )
                        })}
                      </Grid>
                    )}
                </Grid>
              </Box>
              <Box className={classes.spacer} />
              <EditModalActionBar
                canDelete={isEditing}
                onDelete={() => setIsConfirmingDelete(true)}
                onCancel={onClose}
                formikProps={formikProps}
                saveText={isAdding ? 'Add' : 'Save'}
                deleteText="Delete Admin"
              />
            </Form>
          )}
        </Formik>
      )}
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDeleteClicked}
          open={isConfirmingDelete}
          line1Text="Please confirm you wish to delete this admin.  This action cannot be reversed."
          line2Text={`Agency: ${agency.name}`}
          line3Text={`User: ${user.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this admin."
        />
      )}
    </React.Fragment>
  );
}
